'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { createClient } from '@/lib/supabase/client';

interface StorageItem {
  id: string;
  reference_number: string;
  asset_category: string;
  asset_description: string;
  weight_value: number | null;
  weight_unit: string | null;
  purity_grade: string | null;
  declared_value: number;
  currency: string;
  asset_status: string;
  created_at: string;
}

interface CurrencyRate {
  pair: string;
  base: string;
  quote: string;
  rate: number | null;
  change: number | null;
  loading: boolean;
  flag: string;
  label: string;
}

const statusColors: Record<string, string> = {
  in_custody: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  under_inspection: 'bg-amber-50 text-amber-700 border-amber-200',
  verified: 'bg-blue-50 text-blue-700 border-blue-200',
  intake_scheduled: 'bg-slate-50 text-slate-600 border-slate-200',
  withdrawal_requested: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  released: 'bg-slate-50 text-slate-500 border-slate-200',
};

const statusLabels: Record<string, string> = {
  in_custody: 'In Custody',
  under_inspection: 'Under Inspection',
  verified: 'Verified',
  intake_scheduled: 'Intake Scheduled',
  withdrawal_requested: 'Withdrawal Requested',
  released: 'Released',
};

const CURRENCIES: Omit<CurrencyRate, 'rate' | 'change' | 'loading'>[] = [
  { pair: 'GBP/USD', base: 'GBP', quote: 'USD', flag: '🇺🇸', label: 'US Dollar' },
  { pair: 'GBP/EUR', base: 'GBP', quote: 'EUR', flag: '🇪🇺', label: 'Euro' },
  { pair: 'GBP/TRY', base: 'GBP', quote: 'TRY', flag: '🇹🇷', label: 'Turkish Lira' },
  { pair: 'GBP/AED', base: 'GBP', quote: 'AED', flag: '🇦🇪', label: 'UAE Dirham' },
];

export default function TrackPage() {
  const supabase = createClient();
  const [accountNumber, setAccountNumber] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [items, setItems] = useState<StorageItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [rates, setRates] = useState<CurrencyRate[]>(
    CURRENCIES.map(c => ({ ...c, rate: null, change: null, loading: true }))
  );
  const [ratesLastUpdated, setRatesLastUpdated] = useState<Date | null>(null);

  // Fetch exchange rates from open.er-api.com (free, no key needed)
  const fetchRates = useCallback(async () => {
    setRates(prev => prev.map(r => ({ ...r, loading: true })));
    try {
      const res = await fetch('https://open.er-api.com/v6/latest/GBP');
      if (!res.ok) throw new Error('Rate fetch failed');
      const data = await res.json();
      const rateMap: Record<string, number> = data.rates || {};
      setRates(CURRENCIES.map(c => ({
        ...c,
        rate: rateMap[c.quote] ?? null,
        change: null,
        loading: false,
      })));
      setRatesLastUpdated(new Date());
    } catch {
      setRates(prev => prev.map(r => ({ ...r, loading: false })));
    }
  }, []);

  useEffect(() => {
    fetchRates();
    const interval = setInterval(fetchRates, 60_000);
    return () => clearInterval(interval);
  }, [fetchRates]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputValue.trim().toUpperCase();
    if (!trimmed) return;
    setAccountNumber(trimmed);
    setLoadingItems(true);
    setSearched(true);
    setError('');
    setItems([]);

    try {
      // Look up user by account number
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('account_number', trimmed)
        .single();

      if (profileError || !profile) {
        setError('No account found with that account number. Please check and try again.');
        setLoadingItems(false);
        return;
      }

      const { data: assetData, error: assetError } = await supabase
        .from('asset_intakes')
        .select('id, reference_number, asset_category, asset_description, weight_value, weight_unit, purity_grade, declared_value, currency, asset_status, created_at')
        .eq('user_id', profile.id)
        .order('created_at', { ascending: false });

      if (assetError) throw assetError;
      setItems(assetData || []);
    } catch {
      setError('An error occurred while fetching your storage items. Please try again.');
    } finally {
      setLoadingItems(false);
    }
  };

  const inCustody = items.filter(i => ['in_custody', 'verified'].includes(i.asset_status));

  return (
    <div className="min-h-screen bg-[#f8f9fb]">
      {/* Nav */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center">
              <Image
                src="/assets/images/ChatGPT_Image_Jul_15__2026__06_13_59_PM-1784139267730.png"
                alt="Kars Postasi Ltd logo"
                width={36}
                height={36}
                className="object-contain"
                style={{ mixBlendMode: 'multiply' }}
              />
            </div>
            <div>
              <p className="text-slate-900 font-bold text-[14px] leading-tight">KARS POSTASI LTD</p>
              <p className="text-slate-400 text-[9px] uppercase tracking-widest font-600">Security · Privacy · Trust</p>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link href="/sign-up-login-screen" className="text-[13px] font-600 text-slate-600 hover:text-slate-900 transition-colors px-3 py-2 rounded-lg hover:bg-slate-100">
              Sign In
            </Link>
            <Link href="/sign-up-login-screen" className="text-[13px] font-600 text-white bg-slate-900 px-4 py-2 rounded-lg hover:bg-slate-800 transition-all">
              Open Account
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-10">

        {/* Hero */}
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 text-[11px] font-600 text-slate-500 uppercase tracking-widest mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
            Public Tracking Portal
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 leading-tight">Track Your Storage Items</h1>
          <p className="text-[15px] text-slate-500 leading-relaxed">
            Enter your account number to view the current status of all your assets held in custody at Kars Postasi Ltd.
          </p>
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 max-w-2xl mx-auto">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                placeholder="Enter your account number (e.g. KP-000123)"
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-[14px] focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400 bg-slate-50 placeholder:text-slate-400"
              />
            </div>
            <button
              type="submit"
              disabled={loadingItems || !inputValue.trim()}
              className="px-6 py-3 bg-slate-900 text-white text-[14px] font-600 rounded-xl hover:bg-slate-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {loadingItems ? 'Searching…' : 'Track Items'}
            </button>
          </form>
          <p className="text-[11px] text-slate-400 mt-3 text-center">
            Your account number can be found in your welcome email or account settings.
          </p>
        </div>

        {/* Results */}
        {searched && (
          <div className="max-w-4xl mx-auto">
            {loadingItems ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 flex items-center justify-center gap-3">
                <svg className="animate-spin w-5 h-5 text-slate-400" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeDashoffset="12" />
                </svg>
                <span className="text-[14px] text-slate-500">Fetching your storage items…</span>
              </div>
            ) : error ? (
              <div className="bg-white rounded-2xl border border-red-100 p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                </div>
                <p className="text-[14px] font-600 text-slate-700 mb-1">Account Not Found</p>
                <p className="text-[13px] text-slate-500">{error}</p>
              </div>
            ) : items.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                  </svg>
                </div>
                <p className="text-[14px] font-600 text-slate-700 mb-1">No Storage Items Found</p>
                <p className="text-[13px] text-slate-500">Account <span className="font-mono font-600">{accountNumber}</span> has no registered custody items yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Summary bar */}
                <div className="bg-white rounded-2xl border border-slate-200 px-6 py-4 flex flex-wrap items-center gap-4 justify-between">
                  <div>
                    <p className="text-[12px] text-slate-400 uppercase tracking-wide font-600 mb-0.5">Account</p>
                    <p className="text-[15px] font-mono font-700 text-slate-900">{accountNumber}</p>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-[22px] font-bold text-emerald-600">{inCustody.length}</p>
                      <p className="text-[11px] text-slate-400 font-600">In Custody</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[22px] font-bold text-slate-700">{items.length}</p>
                      <p className="text-[11px] text-slate-400 font-600">Total Items</p>
                    </div>
                  </div>
                </div>

                {/* Items list */}
                <div className="space-y-3">
                  {items.map(item => (
                    <div key={item.id} className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="font-mono text-[12px] font-700 text-slate-900 bg-slate-100 px-2 py-0.5 rounded">{item.reference_number}</span>
                          <span className={`text-[11px] font-600 px-2.5 py-0.5 rounded-full border ${statusColors[item.asset_status] ?? 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                            {statusLabels[item.asset_status] ?? item.asset_status}
                          </span>
                          <span className="text-[11px] text-slate-400 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full">{item.asset_category}</span>
                        </div>
                        <p className="text-[14px] font-600 text-slate-800 mb-1">{item.asset_description}</p>
                        <div className="flex flex-wrap gap-3 text-[12px] text-slate-500">
                          {item.weight_value && (
                            <span>Weight: <span className="font-600 text-slate-700">{item.weight_value}{item.weight_unit || 'g'}</span></span>
                          )}
                          {item.purity_grade && (
                            <span>Purity: <span className="font-600 text-slate-700">{item.purity_grade}</span></span>
                          )}
                          <span>Registered: <span className="font-600 text-slate-700">{new Date(item.created_at).toLocaleDateString('en-GB')}</span></span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-[11px] text-slate-400 mb-0.5">Declared Value</p>
                        <p className="text-[18px] font-bold text-slate-900">
                          {new Intl.NumberFormat('en-GB', { style: 'currency', currency: item.currency, maximumFractionDigits: 0 }).format(item.declared_value)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Currency Rates */}
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-[16px] font-700 text-slate-900">Live Currency Rates</h2>
              <p className="text-[12px] text-slate-400 mt-0.5">GBP base · Updates every 60 seconds</p>
            </div>
            <div className="flex items-center gap-2">
              {ratesLastUpdated && (
                <span className="text-[11px] text-slate-400">
                  Updated {ratesLastUpdated.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              <button
                onClick={fetchRates}
                className="w-8 h-8 rounded-lg border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-50 transition-colors"
                title="Refresh rates"
              >
                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {rates.map(r => (
              <div key={r.pair} className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[20px]">{r.flag}</span>
                  <div>
                    <p className="text-[11px] font-700 text-slate-900">{r.pair}</p>
                    <p className="text-[10px] text-slate-400">{r.label}</p>
                  </div>
                </div>
                {r.loading ? (
                  <div className="h-7 bg-slate-100 rounded animate-pulse" />
                ) : r.rate !== null ? (
                  <div>
                    <p className="text-[22px] font-bold text-slate-900 leading-none">
                      {r.rate.toFixed(r.quote === 'TRY' ? 2 : 4)}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-1">1 GBP = {r.rate.toFixed(4)} {r.quote}</p>
                  </div>
                ) : (
                  <p className="text-[13px] text-slate-400">Unavailable</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer CTA */}
        <div className="max-w-4xl mx-auto bg-slate-900 rounded-2xl p-8 sm:p-10 text-center">
          <h3 className="text-[20px] font-bold text-white mb-2">Need to access your full account?</h3>
          <p className="text-[14px] text-slate-400 mb-6">Sign in to manage deposits, request withdrawals, and view your complete portfolio.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link href="/sign-up-login-screen" className="px-6 py-3 bg-white text-slate-900 text-[14px] font-600 rounded-xl hover:bg-slate-100 transition-all">
              Sign In to Dashboard
            </Link>
            <Link href="/sign-up-login-screen" className="px-6 py-3 border border-slate-700 text-white text-[14px] font-600 rounded-xl hover:bg-slate-800 transition-all">
              Open an Account
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center pb-4">
          <p className="text-[12px] text-slate-400">
            © {new Date().getFullYear()} Kars Postasi Ltd · Glasgow, Scotland ·{' '}
            <Link href="/contact" className="hover:text-slate-600 transition-colors">Contact</Link>
            {' · '}
            <Link href="/services" className="hover:text-slate-600 transition-colors">Services</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
